// Rate library — reusable line items with optional full build-up
// (Materials + Labour + Plant + Overhead + Margin). Auto-computes the
// unit_price so the QS builds a rate from first principles.
import { useEffect, useState, useMemo } from "react";
import { PageHeader } from "@/pages/dashboard/_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Library, Plus, Search, Trash2, Save, X, Calculator, Package, Wrench, Users, Tag } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import CivilPublicLibrary from "@/components/CivilPublicLibrary";

// Empty build-up scaffold for a new rate
const EMPTY_BUILDUP = () => ({
  materials: [],
  labour: [],
  plant: [],
  overhead_pct: 10,
  margin_pct: 15,
});

// Client-side mirror of backend _price_from_buildup for live preview
const priceFromBuildUp = (bu) => {
  if (!bu) return 0;
  const m = (bu.materials || []).reduce((s, r) => s + (Number(r.qty) || 0) * (Number(r.unit_cost) || 0), 0);
  const l = (bu.labour || []).reduce((s, r) => s + (Number(r.hours) || 0) * (Number(r.hourly_rate) || 0), 0);
  const p = (bu.plant || []).reduce((s, r) => s + (Number(r.hours) || 0) * (Number(r.hourly_rate) || 0), 0);
  const direct = m + l + p;
  const withOverhead = direct * (1 + (Number(bu.overhead_pct) || 0) / 100);
  const withMargin = withOverhead * (1 + (Number(bu.margin_pct) || 0) / 100);
  return {
    material: +m.toFixed(2),
    labour: +l.toFixed(2),
    plant: +p.toFixed(2),
    direct: +direct.toFixed(2),
    overhead: +(withOverhead - direct).toFixed(2),
    margin: +(withMargin - withOverhead).toFixed(2),
    total: +withMargin.toFixed(2),
  };
};

export default function RateLibrary() {
  const { user } = useAuth();
  const isQS = user?.role === "quantity_surveyor";
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [view, setView] = useState("mine");
  const [publicAllowed, setPublicAllowed] = useState(false);

  useEffect(() => {
    api.get("/civil-library").then(() => setPublicAllowed(true)).catch(() => setPublicAllowed(false));
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, { data: cats }] = await Promise.all([
        api.get("/rate-library"),
        api.get("/rate-library/categories").catch(() => ({ data: [] })),
      ]);
      setItems(data || []);
      setCategories(cats || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (items || []).filter((it) => {
      if (category !== "all" && (it.category || "") !== category) return false;
      if (!q) return true;
      return [it.description, it.unit, it.notes, it.category]
        .filter(Boolean).join(" ").toLowerCase().includes(q);
    });
  }, [items, search, category]);

  const openNew = () => setEditing({
    description: "", unit: "", unit_price: 0, notes: "", category: "",
    build_up: null, useBuildUp: false,
  });
  const openEdit = (it) => setEditing({
    ...it, useBuildUp: !!it.build_up,
    build_up: it.build_up || EMPTY_BUILDUP(),
  });

  const save = async () => {
    if (!editing.description?.trim()) return toast.error("Description is required");
    try {
      const body = {
        description: editing.description.trim(),
        unit: editing.unit || null,
        unit_price: Number(editing.unit_price) || 0,
        notes: editing.notes || null,
        category: (editing.category || "").trim().toLowerCase() || null,
        build_up: editing.useBuildUp ? editing.build_up : null,
      };
      if (editing.id) {
        const { data } = await api.patch(`/rate-library/${editing.id}`, body);
        setItems((p) => p.map((x) => (x.id === data.id ? data : x)));
      } else {
        const { data } = await api.post("/rate-library", body);
        setItems((p) => [data, ...p]);
      }
      // Refresh categories in case a new one was added
      const { data: cats } = await api.get("/rate-library/categories").catch(() => ({ data: [] }));
      setCategories(cats || []);
      toast.success("Saved");
      setEditing(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const del = async (it) => {
    if (!window.confirm(`Remove "${it.description}" from your library?`)) return;
    try {
      await api.delete(`/rate-library/${it.id}`);
      setItems((p) => p.filter((x) => x.id !== it.id));
      toast.success("Removed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="rate-library-page">
      <PageHeader
        tag={isQS ? "/ QS" : "/ Sales"}
        title="Rate library"
        subtitle="Reusable line items with optional build-ups (materials + labour + plant + overhead + margin). Pull straight into any quote or invoice."
        action={
          <Button
            onClick={openNew}
            className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-xs"
            data-testid="rate-new"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add rate
          </Button>
        }
      />

      {publicAllowed && (
        <div className="flex gap-1 mb-4 border-b border-zinc-200 dark:border-white/10" data-testid="rate-lib-tabs">
          {[["mine", "My Rate Library"], ["public", "Civil Public Library 🌐"]].map(([k, label]) => (
            <button key={k} onClick={() => setView(k)}
              className={`px-4 py-2 text-[11px] font-bold uppercase tracking-wider border-b-2 -mb-px ${view === k ? "border-[#FF5F15] text-[#FF5F15]" : "border-transparent text-zinc-500 hover:text-[#FF5F15]"}`}
              data-testid={`rate-lib-tab-${k}`}>{label}</button>
          ))}
        </div>
      )}

      {view === "public" ? (
        <CivilPublicLibrary />
      ) : (
      <>
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search rates..."
            className="pl-9 rounded-sm border-zinc-400"
            data-testid="rate-search"
          />
        </div>
        {categories.length > 0 && (
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-52 rounded-sm border-zinc-400" data-testid="rate-category-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {loading ? (
        <div className="space-y-2">{[1, 2, 3].map((i) => (<div key={i} className="h-14 bg-zinc-100 animate-pulse border border-zinc-200" />))}</div>
      ) : items.length === 0 ? (
        <div className="border-2 border-dashed border-zinc-300 p-12 text-center" data-testid="rate-empty">
          <Library className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
          <div className="font-display font-bold uppercase tracking-tight text-lg">Your rate library is empty</div>
          <p className="text-sm text-zinc-500 mt-1 max-w-md mx-auto">
            Add the line items you quote most often — with or without a full material/labour/plant build-up so future quotes just need a quantity.
          </p>
          <Button
            onClick={openNew}
            className="mt-4 rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-xs"
            data-testid="rate-empty-add"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add your first rate
          </Button>
        </div>
      ) : (
        <div className="border border-zinc-300 bg-white" data-testid="rate-list">
          <table className="w-full text-sm">
            <thead className="bg-zinc-100 border-b border-zinc-300">
              <tr className="text-left text-[10px] font-bold uppercase tracking-widest text-zinc-600">
                <th className="px-3 py-2">Description</th>
                <th className="px-3 py-2">Category</th>
                <th className="px-3 py-2">Unit</th>
                <th className="px-3 py-2 text-right">Unit price</th>
                <th className="px-3 py-2">Build-up</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-200">
              {filtered.map((it) => (
                <tr key={it.id} className="hover:bg-zinc-50" data-testid={`rate-row-${it.id}`}>
                  <td className="px-3 py-2 font-bold">{it.description}</td>
                  <td className="px-3 py-2 text-xs">
                    {it.category ? (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-zinc-300 rounded-sm bg-zinc-50 uppercase tracking-widest text-[9px] font-bold">
                        <Tag className="w-2.5 h-2.5" /> {it.category}
                      </span>
                    ) : "—"}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-zinc-600">{it.unit || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono font-bold">${Number(it.unit_price || 0).toFixed(2)}</td>
                  <td className="px-3 py-2 text-xs">
                    {it.build_up ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700 font-bold uppercase tracking-widest text-[9px]" data-testid={`rate-buildup-badge-${it.id}`}>
                        <Calculator className="w-3 h-3" /> Built up
                      </span>
                    ) : <span className="text-zinc-400 text-[10px] italic">Flat rate</span>}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(it)} className="text-[10px] font-bold uppercase tracking-wider text-zinc-700 hover:text-[#FF5F15] mr-3" data-testid={`rate-edit-${it.id}`}>
                      Edit
                    </button>
                    <button onClick={() => del(it)} className="text-zinc-400 hover:text-red-600" data-testid={`rate-del-${it.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-xs text-zinc-500">No rates match your filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <RateEditor
          editing={editing}
          setEditing={setEditing}
          onSave={save}
          onCancel={() => setEditing(null)}
          categories={categories}
        />
      )}
      </>
      )}
    </div>
  );
}

// ----- Editor with optional build-up -----
function RateEditor({ editing, setEditing, onSave, onCancel, categories }) {
  const buildUp = editing.build_up || EMPTY_BUILDUP();
  const computed = editing.useBuildUp ? priceFromBuildUp(buildUp) : null;

  // Auto-sync unit_price to computed when using build-up
  useEffect(() => {
    if (editing.useBuildUp && computed) {
      setEditing((e) => ({ ...e, unit_price: computed.total }));
    }
  }, [editing.useBuildUp, JSON.stringify(buildUp)]);

  const updateBU = (patch) => setEditing((e) => ({ ...e, build_up: { ...buildUp, ...patch } }));

  const addMaterial = () => updateBU({ materials: [...buildUp.materials, { description: "", qty: 0, unit: "", unit_cost: 0 }] });
  const updMaterial = (i, patch) => updateBU({ materials: buildUp.materials.map((r, idx) => idx === i ? { ...r, ...patch } : r) });
  const rmMaterial = (i) => updateBU({ materials: buildUp.materials.filter((_, idx) => idx !== i) });

  const addLabour = () => updateBU({ labour: [...buildUp.labour, { role: "", hours: 0, hourly_rate: 65 }] });
  const updLabour = (i, patch) => updateBU({ labour: buildUp.labour.map((r, idx) => idx === i ? { ...r, ...patch } : r) });
  const rmLabour = (i) => updateBU({ labour: buildUp.labour.filter((_, idx) => idx !== i) });

  const addPlant = () => updateBU({ plant: [...buildUp.plant, { description: "", hours: 0, hourly_rate: 150 }] });
  const updPlant = (i, patch) => updateBU({ plant: buildUp.plant.map((r, idx) => idx === i ? { ...r, ...patch } : r) });
  const rmPlant = (i) => updateBU({ plant: buildUp.plant.filter((_, idx) => idx !== i) });

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="rate-editor-overlay">
      <div className="bg-white max-w-3xl w-full border-2 border-zinc-300 p-5 my-8">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="label-tag">{editing.id ? "Edit rate" : "New rate"}</div>
            <h2 className="font-display font-black uppercase tracking-tight text-lg mt-1">
              {editing.id ? "Update line item" : "Add to library"}
            </h2>
          </div>
          <button onClick={onCancel} className="text-zinc-400 hover:text-zinc-700" data-testid="rate-editor-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <Label className="label-tag">Description *</Label>
            <Input
              value={editing.description}
              onChange={(e) => setEditing({ ...editing, description: e.target.value })}
              placeholder="Excavation — 20 MPa concrete slab, 150mm thk"
              className="mt-1.5 rounded-sm border-zinc-400"
              data-testid="rate-editor-desc"
            />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="label-tag">Category</Label>
              <Input
                value={editing.category || ""}
                onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                placeholder="earthworks, concrete, drainage…"
                list="rate-category-suggestions"
                className="mt-1.5 rounded-sm border-zinc-400"
                data-testid="rate-editor-category"
              />
              <datalist id="rate-category-suggestions">
                {categories.map((c) => <option key={c} value={c} />)}
              </datalist>
            </div>
            <div>
              <Label className="label-tag">Unit</Label>
              <Input
                value={editing.unit || ""}
                onChange={(e) => setEditing({ ...editing, unit: e.target.value })}
                placeholder="m³ · t · hr · lm · ea"
                className="mt-1.5 rounded-sm border-zinc-400 font-mono"
                data-testid="rate-editor-unit"
              />
            </div>
            <div>
              <Label className="label-tag">Unit price ($NZD)</Label>
              <Input
                type="number"
                step="0.01"
                value={editing.unit_price}
                onChange={(e) => setEditing({ ...editing, unit_price: e.target.value })}
                disabled={editing.useBuildUp}
                className={`mt-1.5 rounded-sm border-zinc-400 font-mono ${editing.useBuildUp ? "bg-zinc-100 text-zinc-500" : ""}`}
                data-testid="rate-editor-price"
              />
              {editing.useBuildUp && (
                <div className="text-[10px] text-zinc-500 mt-0.5">Auto-computed from build-up below</div>
              )}
            </div>
          </div>

          {/* Build-up toggle */}
          <label className="flex items-center gap-2 p-3 border-2 border-zinc-200 rounded-sm cursor-pointer hover:border-[#FF5F15] transition-colors" data-testid="rate-buildup-toggle">
            <input
              type="checkbox"
              checked={!!editing.useBuildUp}
              onChange={(e) => setEditing({ ...editing, useBuildUp: e.target.checked })}
              className="w-4 h-4 accent-[#FF5F15]"
            />
            <Calculator className="w-4 h-4 text-[#FF5F15]" />
            <span className="text-sm font-bold">Build this rate from first principles</span>
            <span className="text-[11px] text-zinc-500 ml-2">Materials + Labour + Plant → auto-calculates the unit price</span>
          </label>

          {editing.useBuildUp && (
            <div className="border-2 border-orange-200 bg-orange-50/40 p-4 rounded-sm space-y-4" data-testid="rate-buildup-panel">
              <BuildUpSection
                title="Materials"
                icon={Package}
                testIdPrefix="material"
                columns={["Description", "Qty per unit", "Unit", "Cost / unit"]}
                rows={buildUp.materials}
                onAdd={addMaterial}
                onUpdate={updMaterial}
                onRemove={rmMaterial}
                total={computed?.material || 0}
                renderRow={(row, i, upd, rm) => (
                  <>
                    <Input className="col-span-5 rounded-sm text-xs" value={row.description} onChange={(e) => upd({ description: e.target.value })} placeholder="e.g. GAP40 hardfill" data-testid={`material-desc-${i}`} />
                    <Input type="number" step="0.001" className="col-span-2 rounded-sm text-xs font-mono text-right" value={row.qty} onChange={(e) => upd({ qty: parseFloat(e.target.value) || 0 })} data-testid={`material-qty-${i}`} />
                    <Input className="col-span-2 rounded-sm text-xs font-mono" value={row.unit || ""} onChange={(e) => upd({ unit: e.target.value })} placeholder="t" data-testid={`material-unit-${i}`} />
                    <Input type="number" step="0.01" className="col-span-2 rounded-sm text-xs font-mono text-right" value={row.unit_cost} onChange={(e) => upd({ unit_cost: parseFloat(e.target.value) || 0 })} data-testid={`material-cost-${i}`} />
                    <button onClick={rm} className="col-span-1 text-zinc-400 hover:text-red-600" data-testid={`material-rm-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </>
                )}
              />
              <BuildUpSection
                title="Labour"
                icon={Users}
                testIdPrefix="labour"
                columns={["Role", "Hours per unit", "Hourly rate"]}
                rows={buildUp.labour}
                onAdd={addLabour}
                onUpdate={updLabour}
                onRemove={rmLabour}
                total={computed?.labour || 0}
                renderRow={(row, i, upd, rm) => (
                  <>
                    <Input className="col-span-6 rounded-sm text-xs" value={row.role} onChange={(e) => upd({ role: e.target.value })} placeholder="e.g. Operator" data-testid={`labour-role-${i}`} />
                    <Input type="number" step="0.01" className="col-span-2 rounded-sm text-xs font-mono text-right" value={row.hours} onChange={(e) => upd({ hours: parseFloat(e.target.value) || 0 })} data-testid={`labour-hours-${i}`} />
                    <Input type="number" step="0.01" className="col-span-3 rounded-sm text-xs font-mono text-right" value={row.hourly_rate} onChange={(e) => upd({ hourly_rate: parseFloat(e.target.value) || 0 })} data-testid={`labour-rate-${i}`} />
                    <button onClick={rm} className="col-span-1 text-zinc-400 hover:text-red-600" data-testid={`labour-rm-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </>
                )}
              />
              <BuildUpSection
                title="Plant"
                icon={Wrench}
                testIdPrefix="plant"
                columns={["Description", "Hours per unit", "Hourly rate"]}
                rows={buildUp.plant}
                onAdd={addPlant}
                onUpdate={updPlant}
                onRemove={rmPlant}
                total={computed?.plant || 0}
                renderRow={(row, i, upd, rm) => (
                  <>
                    <Input className="col-span-6 rounded-sm text-xs" value={row.description} onChange={(e) => upd({ description: e.target.value })} placeholder="e.g. 5T Excavator" data-testid={`plant-desc-${i}`} />
                    <Input type="number" step="0.01" className="col-span-2 rounded-sm text-xs font-mono text-right" value={row.hours} onChange={(e) => upd({ hours: parseFloat(e.target.value) || 0 })} data-testid={`plant-hours-${i}`} />
                    <Input type="number" step="0.01" className="col-span-3 rounded-sm text-xs font-mono text-right" value={row.hourly_rate} onChange={(e) => upd({ hourly_rate: parseFloat(e.target.value) || 0 })} data-testid={`plant-rate-${i}`} />
                    <button onClick={rm} className="col-span-1 text-zinc-400 hover:text-red-600" data-testid={`plant-rm-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </>
                )}
              />

              {/* Overhead + Margin */}
              <div className="grid grid-cols-2 gap-3 pt-2 border-t border-orange-200">
                <div>
                  <Label className="label-tag">Overhead %</Label>
                  <Input type="number" step="0.1" value={buildUp.overhead_pct} onChange={(e) => updateBU({ overhead_pct: parseFloat(e.target.value) || 0 })} className="mt-1.5 rounded-sm border-zinc-400 font-mono text-right" data-testid="rate-overhead-pct" />
                </div>
                <div>
                  <Label className="label-tag">Margin %</Label>
                  <Input type="number" step="0.1" value={buildUp.margin_pct} onChange={(e) => updateBU({ margin_pct: parseFloat(e.target.value) || 0 })} className="mt-1.5 rounded-sm border-zinc-400 font-mono text-right" data-testid="rate-margin-pct" />
                </div>
              </div>

              {/* Live summary */}
              <div className="bg-white border-2 border-[#FF5F15] p-3 rounded-sm" data-testid="rate-buildup-summary">
                <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2">Rate summary — per {editing.unit || "unit"}</div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs font-mono">
                  <div className="flex justify-between"><span>Materials</span><span>${computed?.material.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span>Labour</span><span>${computed?.labour.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span>Plant</span><span>${computed?.plant.toFixed(2)}</span></div>
                  <div className="flex justify-between font-bold"><span>Direct cost</span><span>${computed?.direct.toFixed(2)}</span></div>
                  <div className="flex justify-between text-zinc-600"><span>+ Overhead ({buildUp.overhead_pct}%)</span><span>${computed?.overhead.toFixed(2)}</span></div>
                  <div className="flex justify-between text-zinc-600"><span>+ Margin ({buildUp.margin_pct}%)</span><span>${computed?.margin.toFixed(2)}</span></div>
                </div>
                <div className="mt-3 pt-2 border-t-2 border-[#FF5F15] flex items-baseline justify-between">
                  <span className="font-display font-black uppercase tracking-tight text-sm">Sell rate</span>
                  <span className="font-mono font-black text-2xl text-[#FF5F15]" data-testid="rate-buildup-total">${computed?.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          <div>
            <Label className="label-tag">Notes (optional)</Label>
            <Textarea
              value={editing.notes || ""}
              onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
              rows={2}
              placeholder="Any constraints — e.g. excludes traffic management"
              className="mt-1.5 rounded-sm border-zinc-400 text-sm"
              data-testid="rate-editor-notes"
            />
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} className="rounded-sm uppercase tracking-wider font-bold text-xs">
            Cancel
          </Button>
          <Button onClick={onSave} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider font-bold text-xs" data-testid="rate-editor-save">
            <Save className="w-3.5 h-3.5 mr-1.5" /> Save
          </Button>
        </div>
      </div>
    </div>
  );
}

function BuildUpSection({ title, icon: Icon, testIdPrefix, columns, rows, onAdd, onUpdate, onRemove, total, renderRow }) {
  return (
    <div data-testid={`buildup-section-${testIdPrefix}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-[#FF5F15]" />
          <span className="font-display font-black uppercase tracking-tight text-sm">{title}</span>
          <span className="text-[10px] font-mono text-zinc-500">${total.toFixed(2)}</span>
        </div>
        <Button variant="outline" size="sm" onClick={onAdd} className="rounded-sm text-[10px] h-7 uppercase tracking-wider font-bold" data-testid={`${testIdPrefix}-add`}>
          <Plus className="w-3 h-3 mr-1" /> Add {title.toLowerCase().slice(0, -1)}
        </Button>
      </div>
      {rows.length === 0 ? (
        <div className="text-[10px] text-zinc-400 italic px-2 py-1">No {title.toLowerCase()} lines yet.</div>
      ) : (
        <div className="grid grid-cols-12 gap-1 text-[9px] font-bold uppercase tracking-widest text-zinc-500 px-1 mb-1">
          {columns.map((c, i) => (
            <div
              key={c}
              className={
                i === 0 ? "col-span-5" :
                (columns.length === 4 ? (i === 1 ? "col-span-2 text-right" : i === 2 ? "col-span-2" : "col-span-2 text-right") :
                                          (i === 0 ? "col-span-6" : i === 1 ? "col-span-2 text-right" : "col-span-3 text-right"))
              }
            >{c}</div>
          ))}
          <div className="col-span-1" />
        </div>
      )}
      <div className="space-y-1">
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-12 gap-1 items-center" data-testid={`${testIdPrefix}-row-${i}`}>
            {renderRow(r, i, (patch) => onUpdate(i, patch), () => onRemove(i))}
          </div>
        ))}
      </div>
    </div>
  );
}
